import { FeatureFlagEvaluation } from "@wingspanhq/wingspan-api-sdk";
import { useWSQuery, WSQueryConfig } from "@ws-react-query";
import { featureFlagsApi } from "@api";
import { queriesFeatureFlags } from "@query";
import { IS_LOCAL_ENV } from "../../shared/constants/environment";
import { getAnonymousStamp } from "../../utils/anonymousStamp";
import { WSServiceError } from "../../utils/serviceHelper";
import { getFeatureFlagsQueryKey } from "./featureFlagsQueryKey";
import { useSession } from "./useSession";

export {
  QUERY_FEATURE_FLAGS,
  getFeatureFlagsQueryKey
} from "./featureFlagsQueryKey";

export const experimentFlags = {
  qboOnboarding: IS_LOCAL_ENV,
  editAllInvoices: IS_LOCAL_ENV,
  collaboratorOnboardingRequirements: IS_LOCAL_ENV,
  purchaseOrderNumber: IS_LOCAL_ENV,
  deduplicateCollaboratorList: IS_LOCAL_ENV,
  physicalCardReplace: IS_LOCAL_ENV,
  electronic1099ConsentRequired: IS_LOCAL_ENV,
  forceShowQBO: IS_LOCAL_ENV,
  autopayV1: IS_LOCAL_ENV,
  meridio: IS_LOCAL_ENV,
  reports: IS_LOCAL_ENV,
  invoicePaymentsDetails: IS_LOCAL_ENV,
  refundButton: IS_LOCAL_ENV,
  organizationCollaboratorExternalIdResolution: IS_LOCAL_ENV,
  refundsPane: IS_LOCAL_ENV,
  nec1099AmountFilters: IS_LOCAL_ENV,
  taxFilingSeason2025: IS_LOCAL_ENV,
  showArAgingReport: IS_LOCAL_ENV,
  prefundingMethods: IS_LOCAL_ENV,
  showCustomFieldFIltersInContractors: IS_LOCAL_ENV,
  flatfilePlatform: IS_LOCAL_ENV,
  editContractorEmail: IS_LOCAL_ENV,
  monitoredEntityChange: IS_LOCAL_ENV,
  paymentCardAsPaymentMethod: IS_LOCAL_ENV,
  paymentCardsCPE: IS_LOCAL_ENV,
  allowDefaultEngagementPaymentsFallback: IS_LOCAL_ENV,
  nec1099season2024: IS_LOCAL_ENV,
  enableContractorOrgExpansion: IS_LOCAL_ENV,
  currencyConversionToUSD: IS_LOCAL_ENV,
  nec1099season2024generation: IS_LOCAL_ENV,
  taxDocumentsSeason2024List: IS_LOCAL_ENV,
  taxDocumentsSeason2024PayerCorrections: IS_LOCAL_ENV,
  flatfileEngagements: IS_LOCAL_ENV,
  achPayerAuthorization: IS_LOCAL_ENV,
  cashback2025: IS_LOCAL_ENV,
  internationalIP: IS_LOCAL_ENV,
  identityVerificationRequirement: IS_LOCAL_ENV,
  requirementsBulkReview: IS_LOCAL_ENV,
  is1099FilingPaused: IS_LOCAL_ENV,
  platformBalance: IS_LOCAL_ENV,
  taxWithholdingOnboarding: IS_LOCAL_ENV,
  taxWithholdingInvoicingAlerts: IS_LOCAL_ENV,
  taxWithholdingDashboardPromotion: IS_LOCAL_ENV,
  walletDashboardPromotion: IS_LOCAL_ENV,
  bankingAdditionalInfo: IS_LOCAL_ENV,
  masterPolicyEnrollment: IS_LOCAL_ENV,
  wayfinderSmartAdvanced: IS_LOCAL_ENV,
  oAuth: IS_LOCAL_ENV,
  onboardingEDDquestionnaire: IS_LOCAL_ENV,
  customBenefitsLloydCapital: false,
  rateCardOverride: IS_LOCAL_ENV,
  displayWorkDefinitions: IS_LOCAL_ENV,
  meridioInAppEnrollment: IS_LOCAL_ENV,
  internationalUsPerson: IS_LOCAL_ENV,
  requirementEditing: IS_LOCAL_ENV
};

export const anonExperimentFlags = {
  paymentCardsCPEGuest: IS_LOCAL_ENV
};

type ExperimentFlags = typeof experimentFlags;
type AnonExperimentFlags = typeof anonExperimentFlags;

export interface IFeatureFlags extends ExperimentFlags, AnonExperimentFlags {}

const applyEnabledFlags = <T extends Record<string, boolean>>(
  enabledFlagNames: string[],
  defaultFlags: T
): T => {
  const enabled = new Set(enabledFlagNames);

  const updated = Object.fromEntries(
    Object.entries(defaultFlags).map(([name, defaultValue]) => [
      name,
      defaultValue || enabled.has(name)
    ])
  );

  return updated as T;
};

const listEnabledFlagNames = async (
  evaluate: () => Promise<FeatureFlagEvaluation>
): Promise<string[]> => {
  try {
    const { enabledFlags } = await evaluate();

    return enabledFlags;
  } catch {
    return [];
  }
};

export type FeatureFlags = typeof experimentFlags & typeof anonExperimentFlags;

export const useQueryFeatureFlags = (
  queryConfig?: WSQueryConfig<FeatureFlags, WSServiceError>
) => {
  const { personId } = useSession();
  const anonymousId = getAnonymousStamp();

  return useWSQuery(
    getFeatureFlagsQueryKey(personId),
    async () => {
      const [anonymousFlagNames, personFlagNames] = await Promise.all([
        listEnabledFlagNames(() =>
          featureFlagsApi.evaluateAnonymousFeatureFlags({
            anonymousFeatureFlagEvaluationRequest: { anonymousId }
          })
        ),
        personId
          ? listEnabledFlagNames(() =>
              queriesFeatureFlags.listPersonFeatureFlags.forceFetchQueryData({
                personId
              })
            )
          : []
      ]);

      return applyEnabledFlags([...anonymousFlagNames, ...personFlagNames], {
        ...experimentFlags,
        ...anonExperimentFlags
      });
    },
    {
      ...queryConfig,
      retry: false,
      retryOnMount: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false
    }
  );
};
